import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import LOGO from '../assets/logo.jpeg';
import Illustration from '../assets/login-illustration.jpeg';

const Register = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const password = watch('password');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      const response = await sendApiRequest(
        'api/auth/register',
        {
          name: data.fullName,
          email: data.email,
          mobile: data.mobile,
          city: data.city,
          password: data.password
        },
        'POST'
      );

      if (!response || response.status === false || !response?.data?.token) {
        throw new Error(response?.message || 'Registration failed');
      }

      localStorage.setItem('auth_token', response.data.token);
      login(response.data.user);
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EAF0FF] to-[#DDE6FF] flex items-center justify-center px-4 py-4">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden">

        <div className="grid grid-cols-1 md:grid-cols-2">

          {/* ================= LEFT PANEL ================= */}
          <div className="relative flex flex-col items-center justify-center bg-[#F5F7FF]">
            <img
              src={LOGO}
              alt="Logo"
              className="h-32 sm:h-40 mb-4 sm:mb-6"
            />

            <img
              src={Illustration}
              alt="Illustration"
              className="max-w-[220px] sm:max-w-sm md:max-w-xs lg:max-w-sm"
            />

            {/* Footer strip */}
            <div className="absolute bottom-0 left-0 w-full bg-[#2F5DA8] text-white text-center py-2 sm:py-3 text-sm sm:text-base font-semibold">
              By. Parihar Sir, Private Secretary High Court
            </div>
          </div>

          {/* ================= RIGHT PANEL ================= */}
          <div className="relative p-2 sm:p-4 md:p-4 bg-[#F5F7FF]">
            {/* Decorative circles */}

            <div className="relative z-10">
              <h2 className="text-xl sm:text-2xl font-bold text-[#2F5DA8] text-center">
                Create a New Account
              </h2>
              <p className="text-center text-gray-600 mb-2 sm:mb-2 text-sm sm:text-base">
                Register a new account to get started.
              </p>

              {error && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-2 sm:space-y-2">

                <input
                  {...register('fullName', { required: 'Full name is required' })}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />
                {errors.fullName && <p className="text-red-500 text-xs sm:text-sm">{errors.fullName.message}</p>}

                <input
                  {...register('city', { required: 'City is required' })}
                  placeholder="Enter your city"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />
                {errors.city && <p className="text-red-500 text-xs sm:text-sm">{errors.city.message}</p>}

                <input
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />
                {errors.email && <p className="text-red-500 text-xs sm:text-sm">{errors.email.message}</p>}

                <input
                  {...register('mobile', { required: 'Mobile number is required' })}
                  placeholder="Enter your mobile number"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />
                {errors.mobile && <p className="text-red-500 text-xs sm:text-sm">{errors.mobile.message}</p>}

                <input
                  type="password"
                  {...register('password', { required: 'Password is required' })}
                  placeholder="Create a password"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />

                <input
                  type="password"
                  {...register('confirmPassword', {
                    validate: value => value === password || 'Passwords do not match'
                  })}
                  placeholder="Confirm password"
                  className="w-full px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-[#088395]/40"
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs sm:text-sm">{errors.confirmPassword.message}</p>
                )}

                <label className="flex items-start text-xs sm:text-sm text-[#088395]">
                  <input
                    type="checkbox"
                    {...register('agreeToTerms', { required: true })}
                    className="mt-1 mr-2"
                  />
                  I agree to the&nbsp;
                  <Link to="/terms" className="text-[#F7374F] font-medium">
                    Terms & Conditions
                  </Link>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 sm:py-3 bg-[#2F5DA8] text-white rounded-lg font-semibold hover:opacity-90 transition"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>

              <p className="text-center text-xs sm:text-sm mt-3 sm:mt-3 text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#2F5DA8] font-medium">
                  Login
                </Link>
              </p>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Register;
